import { forwardRef, memo, type ChangeEvent, type HTMLProps } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import Icons from '../../Elements/Icons/index.ts';
import { InfoTooltipIcon } from '../../Feedback/InfoTooltipIcon/index.tsx';
import { Knob, Track, ToggleTrack, ToggleWrapper, Text } from './styled.ts';

export { ToggleField } from './ToggleField.tsx';

export interface ToggleProps extends Omit<HTMLProps<HTMLInputElement>, 'onChange'> {
  onChange: (value: boolean) => void;
  text?: string;
  largeText?: boolean;
  tooltip?: string;
  inline?: boolean;
  pseudoLabel?: boolean;
  dataTestId?: string;
}

export const Toggle = memo(
  forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
    { checked, onChange, text, largeText, disabled, tooltip, inline, pseudoLabel, dataTestId, ...props },
    ref,
  ) {
    const handleChange = useCallbackRef((e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.checked);
    });

    return (
      <ToggleWrapper $disabled={disabled} $inline={inline} $pseudoLabel={pseudoLabel} data-testid={dataTestId}>
        <ToggleTrack data-test-checked={checked || false}>
          <input checked={checked} onChange={handleChange} type='checkbox' disabled={disabled} ref={ref} id={props.name} {...props} />
          <Track>
            <Knob>
              {checked ? <Icons.CircleCheckmark size={14} color='currentColor' /> : <Icons.CircleMinus size={14} color='currentColor' />}
            </Knob>
          </Track>
        </ToggleTrack>
        {text && (
          <Text $disabled={disabled} $large={largeText}>
            {text}
            {tooltip ? <InfoTooltipIcon tooltip={tooltip} /> : null}
          </Text>
        )}
      </ToggleWrapper>
    );
  }),
);
